import {
  type Factiii,
  type FactiiiSearchResult,
  type FactiiiTag,
  type PinnedFactiii,
  type PostFactiii,
  type SpaceTimeTag
} from '@shared/all/domains/factiiis';
import { type RouterOutputs } from '@shared/all/domains/trpc';

export const formatNumbers = (num: number) => {
  if (num >= 1e9) {
    return truncateToDecimal(num / 1e9) + 'B';
  }
  if (num >= 1e6) {
    return truncateToDecimal(num / 1e6) + 'M';
  }
  if (num >= 1e3) {
    return truncateToDecimal(num / 1e3) + 'K';
  }
  return num.toLocaleString();
};

// Truncate to 1 decimal place without rounding (e.g. 1.05 → "1.05", 1.9 → "1.9")
// Show up to 2 decimal places so small remainders aren't lost
const truncateToDecimal = (n: number): string => {
  const truncated = Math.floor(n * 100) / 100;
  if (truncated % 1 === 0) return String(truncated);
  // Remove trailing zeros (1.50 → "1.5", 1.05 → "1.05")
  return truncated.toFixed(2).replace(/0+$/, '');
};

// Pick the effective max-output-tokens: explicit override wins, then the model's
// own default (defaultMaxOutputTokens). If neither is set we have no upper
// bound, so we return 0 from cost helpers (display as "0-0" / no charge).
const effectiveMaxTokens = (
  replyConfig:
    | {
        maxOutputTokens: number | null;
        model?: { defaultMaxOutputTokens?: number | null };
      }
    | null
    | undefined
): number | null => {
  if (!replyConfig) return null;
  return (
    replyConfig.maxOutputTokens ??
    replyConfig.model?.defaultMaxOutputTokens ??
    null
  );
};

// Worst-case rate for an estimate: the whole output budget billed at the
// OUTPUT rate, which is the most expensive class (~5x input). Mirrors the
// server's affordability gate in generateFactiiiBotReply. Falls back to the
// legacy blended `cost` for models that haven't been repriced per-class yet.
const worstCaseRate = (model: {
  cost?: unknown;
  outputCost?: unknown;
}): number => Number(model.outputCost ?? model.cost);

export const calculateCostRange = (
  replyConfig: FactiiiSearchResult['replyConfig']
) => {
  if (!replyConfig) return '0-0';
  const tokens = effectiveMaxTokens(replyConfig);
  const { model } = replyConfig;
  if (!tokens || !model?.cost || !model?.perTokens) return '0-0';

  const maxCost = Math.ceil(
    (tokens / Number(model.perTokens)) * worstCaseRate(model)
  );
  return `0-${maxCost.toLocaleString()}`;
};

const FREE_LIMIT = 10;
const COST_PER_FACTIII = 500;

// Calculate max bot cost for a single factiii's reply config
const calculateBotCost = (replyConfig: FactiiiTag['replyConfig']): number => {
  if (!replyConfig) return 0;
  const tokens = effectiveMaxTokens(replyConfig);
  const { model } = replyConfig;
  if (!tokens || !model?.cost || !model?.perTokens) return 0;
  return Math.ceil((tokens / Number(model.perTokens)) * worstCaseRate(model));
};

// Tron cost for editing factiiis on an existing post (handles additions +
// removals). `awards` are staged award gives (cost × count each) — they're
// charged from the user's balance like the tag fee, not the bot pool.
export const calculateEditTronCostBreakdown = (
  myPostFactiiiCounts: FactiiiTag[],
  postFactiiisCount: PostFactiii[] | undefined,
  awards?: { cost: number; count: number }[]
) => {
  const existingFactiiiIds = new Set(
    postFactiiisCount?.map((pfc) => pfc.factiiiId) || []
  );
  const countsMap = new Map(
    postFactiiisCount?.map((pfc) => [pfc.factiiiId, pfc.count ?? 1]) || []
  );

  const additionsToCreate = myPostFactiiiCounts.filter(
    (f) => !existingFactiiiIds.has(f.factiiiId) && !f.isRemovedFactiii
  ).length;

  const removalsToDelete = Array.from(existingFactiiiIds)
    .filter((factiiiId) => {
      const inSelection = myPostFactiiiCounts.find(
        (f) => f.factiiiId === factiiiId
      );
      return !inSelection || inSelection.isRemovedFactiii;
    })
    .filter((factiiiId) => countsMap.get(factiiiId) === 1).length;

  let tagEditFee = 0;
  if (existingFactiiiIds.size > FREE_LIMIT && removalsToDelete) {
    const aboveFreeLimit = Math.max(0, existingFactiiiIds.size - FREE_LIMIT);
    tagEditFee += Math.min(removalsToDelete, aboveFreeLimit) * COST_PER_FACTIII;
  }

  const sizeAfterRemovals = existingFactiiiIds.size - removalsToDelete;
  if (sizeAfterRemovals + additionsToCreate > FREE_LIMIT) {
    const finalCount = sizeAfterRemovals + additionsToCreate;
    const chargedAdditions = Math.max(
      0,
      finalCount - Math.max(sizeAfterRemovals, FREE_LIMIT)
    );
    tagEditFee += chargedAdditions * COST_PER_FACTIII;
  }

  // Bot costs for newly added bot factiiis only — an already-attached bot
  // doesn't re-run, so don't re-charge it. Skip anything not freshly added
  // (matches the "Already added" vs "Newly added" split in the UI).
  let botCost = 0;
  for (const f of myPostFactiiiCounts) {
    if (f.isRemovedFactiii || !f.replyConfig) continue;
    if (!f.isNewFactiii || existingFactiiiIds.has(f.factiiiId)) continue;
    botCost += calculateBotCost(f.replyConfig);
  }

  const awardCost = (awards ?? []).reduce(
    (sum, a) => sum + a.cost * a.count,
    0
  );

  return {
    tagEditFee,
    botCost,
    awardCost,
    total: tagEditFee + botCost + awardCost
  };
};

// Total tron cost for editing factiiis on an existing post (additions + removals + bot costs).
export const calculateEditTronCost = (
  myPostFactiiiCounts: FactiiiTag[],
  postFactiiisCount: PostFactiii[] | undefined
): number =>
  calculateEditTronCostBreakdown(myPostFactiiiCounts, postFactiiisCount).total;

// Tron cost for a new post (no existing factiiis to track).
export const calculateNewPostTronCostBreakdown = (
  myPostFactiiiCounts: FactiiiTag[]
) => {
  const activeFactiiis = myPostFactiiiCounts.filter((f) => !f.isRemovedFactiii);
  const tagEditFee =
    Math.max(0, activeFactiiis.length - FREE_LIMIT) * COST_PER_FACTIII;
  let botCost = 0;
  for (const f of activeFactiiis) {
    botCost += calculateBotCost(f.replyConfig);
  }
  return { tagEditFee, botCost, total: tagEditFee + botCost };
};

export const calculateNewPostTronCost = (
  myPostFactiiiCounts: FactiiiTag[]
): number => calculateNewPostTronCostBreakdown(myPostFactiiiCounts).total;

export function convertSpaceTimeTagToBackend(
  spaceTime: SpaceTimeTag
): RouterOutputs['factiiis']['myFactiiiTags'][number]['postFactiiis'][number]['spaceTimes'][number] {
  return {
    id: spaceTime.id ?? 0,
    timestamp:
      typeof spaceTime.timestamp === 'string'
        ? new Date(spaceTime.timestamp)
        : (spaceTime.timestamp ?? null),
    latitude: spaceTime.latitude ?? null,
    longitude: spaceTime.longitude ?? null,
    display: spaceTime.display ?? null,
    altitude: spaceTime.altitude ?? null,
    deviceVerifiedAt: spaceTime.deviceVerifiedAt ?? null
  };
}

export const convertToFactiiiTag = (
  bot: FactiiiSearchResult | Factiii
): FactiiiTag => {
  return {
    id: bot.id, // Fake but not needed
    factiiiId: bot.id,
    name: bot.name,
    isRetired: bot.status === 'RETIRED',
    isBot: true,
    isNewFactiii: true,
    isRemovedFactiii: false,
    isPinnedFactiii: false,
    avatarKey: bot.avatarKey,
    requirements: bot.requirements,
    spaceSlug: bot.spaceSlug,
    slug: bot.slug,
    description: bot.description,
    replyConfig: bot.replyConfig,
    postFactiiis: [
      {
        data: null,
        number: null,
        spaceTimes: [],
        uploads: [],
        error: false,
        factiiiReferences: [],
        references: [],
        anonymous: false,
        photo: undefined
      }
    ]
  };
};

export const convertPinnedFactiiiToFactiiiTag = (
  pinnedFactiii: PinnedFactiii
): FactiiiTag => {
  return {
    id: pinnedFactiii.id, // Fake but not needed
    isRetired: false, // pinned factiiis are APPROVED only (helpers/space.ts)
    isNewFactiii: true,
    isRemovedFactiii: false,
    isBot: false,
    isPinnedFactiii: true,
    factiiiId: pinnedFactiii.id,
    name: pinnedFactiii.name,
    avatarKey: pinnedFactiii.avatarKey,
    requirements: pinnedFactiii.requirements,
    spaceSlug: pinnedFactiii.spaceSlug,
    slug: pinnedFactiii.slug,
    description: pinnedFactiii.description,
    replyConfig: undefined,
    postFactiiis: [
      {
        data: null,
        number: null,
        spaceTimes: [],
        uploads: [],
        error: false,
        references: [],
        factiiiReferences: [],
        anonymous: false,
        photo: undefined
      }
    ]
  };
};

// What an award does to the giver's balance, as every award card says it.
export const awardGiveText = (cost: number, count = 1): string =>
  count > 1
    ? `Gives the author ${cost.toLocaleString()} trons each from your balance · ${(cost * count).toLocaleString()} total`
    : `Gives the author ${cost.toLocaleString()} trons from your balance`;

// The notice above the Tag button while awards are staged.
export const awardTagNotice = (
  awards: { cost: number; count: number }[],
  author?: string
): string => {
  const total = awards.reduce((sum, a) => sum + a.cost * a.count, 0);
  return `Tag gives ${author ? `@${author}` : 'the author'} ${total.toLocaleString()} trons from your balance.`;
};

// Awards "pretend to be factiiis": parse one into a FactiiiTag so it renders
// through the same card. The `award` field drives the avatar/cost rendering —
// the card shows the trons cost on its own line (like a bot), so it stays out
// of the description here.
export const convertAwardToFactiiiTag = (award: {
  id: number;
  title: string;
  image: string | null | undefined;
  description?: string | null;
  cost: number;
  anonymous?: boolean;
  count?: number;
}): FactiiiTag => {
  return {
    id: award.id,
    isRetired: false,
    isNewFactiii: true,
    isRemovedFactiii: false,
    isBot: false,
    isPinnedFactiii: false,
    factiiiId: award.id,
    name: award.title,
    // Award image goes in avatarKey so the SAME Avatar renders it as a factiii.
    avatarKey: award.image ?? null,
    requirements: [],
    spaceSlug: undefined,
    slug: '',
    description: award.description ?? null,
    replyConfig: undefined,
    postFactiiis: [
      {
        data: null,
        number: null,
        spaceTimes: [],
        uploads: [],
        error: false,
        references: [],
        factiiiReferences: [],
        anonymous: false,
        photo: undefined
      }
    ],
    award: {
      image: award.image,
      cost: award.cost,
      anonymous: award.anonymous ?? false,
      count: award.count ?? 1
    }
  };
};

export const convertMyFactiiiTagsDataToFactiiiTag = (
  tag: RouterOutputs['factiiis']['myFactiiiTags'][number]
): FactiiiTag => {
  const first = tag.postFactiiis[0];
  return {
    ...tag,
    isNewFactiii: false,
    isRemovedFactiii: false,
    isPinnedFactiii: false,
    postFactiiis: [
      {
        ...first,
        uploads: first.uploads.map((u) => ({
          id: u.id,
          display: u.display,
          // Dead hot-link: the editor shows the placeholder, not a broken image.
          ...(u.sourceMissing && { sourceMissing: true })
        })),
        spaceTimes: first.spaceTimes.map((st) => ({
          ...st,
          locationValue: `${st.latitude},${st.longitude}`
        }))
      }
    ]
  };
};
